import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AccountValidationLoading from '../../components/AccountValidationLoading';
import { messages } from '../../data/messages';
import { getLanguage } from '../../utils/languageStorage';
import { Config } from '../../common/config';
import { fetchCodeDetailList } from '../../common/fetchCodeDetailList';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 71 }, (_, idx) => currentYear - idx);
const months = Array.from({ length: 12 }, (_, idx) => idx + 1);
const days = Array.from({ length: 31 }, (_, idx) => idx + 1);

export default function AccountValidationPopup() {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang') || getLanguage();
  const msg = messages[lang === 'en' ? 'en' : 'ko'];
  // 법인(BT003): 사업자번호로 계좌 인증 (LGD_GUBUN=5), 그 외: 예금주+생년월일 (LGD_GUBUN=3)
  const isCorporate = params.get('bizType') === 'BT003';
  const bizNo = (params.get('bizNo') || '').replace(/\D/g, '');
  const formattedBizNo =
    bizNo.length === 10
      ? `${bizNo.slice(0, 3)}-${bizNo.slice(3, 5)}-${bizNo.slice(5)}`
      : bizNo;
  const [name, setName] = useState(params.get('name') || '');
  const [year, setYear] = useState(params.get('year') || '');
  const [month, setMonth] = useState(params.get('month') || '');
  const [day, setDay] = useState(params.get('day') || '');
  const [bankCode, setBankCode] = useState(params.get('bankCode') || '');
  const [accountNo, setAccountNo] = useState(params.get('accountNo') || '');

  const [bankOptions, setBankOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const loaderTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadBanks = async () => {
      try {
        const list = await fetchCodeDetailList(
          Config.API_CODE_DETAIL,
          'NG11',
          url => axios.get(url, { withCredentials: true }),
        );
        if (!cancelled && list.length > 0) {
          setBankOptions(list);
        }
      } catch (e) {
        try {
          const openerBanks = window.opener?.__NAEGIFT_BANK_OPTIONS__;
          if (!cancelled && openerBanks?.length) {
            setBankOptions(openerBanks);
          }
        } catch {
          // ignore
        }
      }
    };

    loadBanks();
    return () => {
      cancelled = true;
    };
  }, []);

  const getBankNameByCode = code => {
    if (!code) return '';
    const bank = bankOptions.find(item => item.detailCode === code);
    return bank ? bank.detailCodeName : '';
  };

  const formatBankCodeForApi = code => {
    if (!code) return '';
    return code.startsWith('B0') ? code.substring(2) : code;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('loading');
    setMessage('');
    const startTime = Date.now();

    const birth =
      year && month && day
        ? `${String(year).slice(-2)}${String(month).padStart(2, '0')}${String(
            day
          ).padStart(2, '0')}`
        : '';
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVER_ADMIN}account-validation`,
        {
          bankCode: formatBankCodeForApi(bankCode),
          accountNo: accountNo,
          ...(!isCorporate && { accountHolder: name }),
          privateNo: isCorporate ? bizNo : birth,
          ...(isCorporate && { bizType: 'BT003' }),
        },
        { withCredentials: true }
      );
      const result = response?.data?.data?.data;
      if (response?.data?.resultCode === 200 && result?.isValid) {
        setStatus('success');
        setMessage(msg.accountValidationSuccess);
      } else {
        setStatus('error');
        setMessage(result?.responseMessage || msg.accountValidationFail);
      }
    } catch (error) {
      setStatus('error');
      const serverMsg =
        error.response?.data?.message ||
        error.response?.data?.resultMessage ||
        error.message;
      setMessage(
        serverMsg && serverMsg !== 'Network Error'
          ? serverMsg
          : msg.accountValidationError
      );
    } finally {
      const elapsed = Date.now() - startTime;
      const MIN_MS = 4000;
      const delay = Math.max(0, MIN_MS - elapsed);
      if (loaderTimerRef.current) {
        clearTimeout(loaderTimerRef.current);
      }
      loaderTimerRef.current = setTimeout(() => {
        setIsLoading(false);
      }, delay);
    }
  };

  const handleConfirmClose = () => {
    if (window.opener) {
      window.opener.postMessage(
        status === 'success'
          ? {
              type: 'ACCOUNT_VALIDATION_RESULT',
              success: true,
              data: {
                bankCode,
                accountNo,
                accountHolder: isCorporate ? '' : name,
                bizType: isCorporate ? 'BT003' : 'BT002',
                ...(isCorporate && { bizNo }),
              },
            }
          : {
              type: 'ACCOUNT_VALIDATION_RESULT',
              success: false,
              errorMessage: message || msg.accountValidationFailShort,
            },
        '*'
      );
    }
    window.close();
  };

  const ResultView = ({ success }) => {
    const isSuccess = success;
    return (
      <div
        style={{
          width: 360,
          maxWidth: '90vw',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          padding: '32px 24px',
          textAlign: 'center',
        }}
      >
        {/* 큰 아이콘: 성공=초록 체크, 실패=빨간 X */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            margin: '0 auto 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isSuccess
              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            boxShadow: isSuccess
              ? '0 4px 20px rgba(34, 197, 94, 0.4)'
              : '0 4px 20px rgba(239, 68, 68, 0.4)',
          }}
        >
          <span
            style={{
              fontSize: 44,
              color: '#ffffff',
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            {isSuccess ? '✓' : '✕'}
          </span>
        </div>
        {/* 결과 문구 */}
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#111827',
            lineHeight: 1.5,
            marginBottom: 28,
          }}
        >
          {isSuccess
            ? msg.accountSafeRegistered
            : message || msg.accountMismatch}
        </div>
        {/* 확인 버튼 */}
        <button
          type="button"
          onClick={handleConfirmClose}
          style={{
            width: '100%',
            padding: '14px 24px',
            fontSize: 16,
            fontWeight: 600,
            color: '#ffffff',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            background: isSuccess
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
              : '#6b7280',
          }}
        >
          {msg.confirm}
        </button>
      </div>
    );
  };

  useEffect(() => {
    return () => {
      if (loaderTimerRef.current) {
        clearTimeout(loaderTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f7fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AccountValidationLoading
        open={isLoading}
        name={name}
        birthY={year}
        birthM={month}
        birthD={day}
        bizNo={isCorporate ? formattedBizNo : ''}
        bankName={getBankNameByCode(bankCode)}
        accountNo={accountNo}
        language={lang}
      />
      {!isLoading && (status === 'success' || status === 'error') ? (
        <ResultView success={status === 'success'} />
      ) : (
        <div
          style={{
            width: 360,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
            padding: 24,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 18,
                color: '#111827',
                lineHeight: 1.5,
              }}
            >
              {isCorporate
                ? msg.corpAccountValidationTitle
                : msg.accountValidationTitle}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.6,
              }}
            >
              {isCorporate
                ? msg.corpAccountValidationDesc1
                : msg.accountValidationDesc1}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.6,
              }}
            >
              {isCorporate
                ? msg.corpAccountValidationDesc2
                : msg.accountValidationDesc2}
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            {/* 법인(GUBUN 5)은 예금주를 검증하지 않음 — 예금주(법인명)는 인증 후 가입 폼에서 직접 입력 */}
            {!isCorporate && (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ display: 'block', fontSize: 12, color: '#666' }}
                >
                  {msg.nameLabel}
                </label>
                <div style={{ width: '100%' }}>
                  <input
                    className="input_01"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            )}
            {isCorporate ? (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ display: 'block', fontSize: 12, color: '#666' }}
                >
                  {msg.bizNoLabel}
                </label>
                <div style={{ width: '100%' }}>
                  <input
                    className="input_01"
                    type="text"
                    value={formattedBizNo}
                    readOnly
                    disabled
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ display: 'block', fontSize: 12, color: '#666' }}
                >
                  {msg.birthLabel}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    className="input_01"
                    value={year}
                    onChange={e => setYear(e.target.value)}
                    required
                  >
                    <option value="">{msg.yearPlaceholder}</option>
                    {years.map(y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input_01"
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    required
                  >
                    <option value="">{msg.monthPlaceholder}</option>
                    {months.map(m => (
                      <option key={m} value={String(m).padStart(2, '0')}>
                        {String(m).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input_01"
                    value={day}
                    onChange={e => setDay(e.target.value)}
                    required
                  >
                    <option value="">{msg.dayPlaceholder}</option>
                    {days.map(d => (
                      <option key={d} value={String(d).padStart(2, '0')}>
                        {String(d).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                {msg.bankLabel}
              </label>
              <div style={{ width: '100%' }}>
                <select
                  className="input_01"
                  value={bankCode}
                  onChange={e => setBankCode(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">{msg.bankSelectNone}</option>
                  {bankOptions.map((bank, index) => (
                    <option key={index} value={bank.detailCode}>
                      {bank.detailCodeName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                {msg.accountNoLabel}
              </label>
              <div style={{ width: '100%' }}>
                <input
                  className="input_01"
                  type="text"
                  maxLength={20}
                  value={accountNo}
                  onChange={e =>
                    setAccountNo(e.target.value.replace(/[^0-9]/g, ''))
                  }
                  required
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                color: status === 'error' ? '#d32f2f' : '#4a5568',
                minHeight: 20,
              }}
            >
              {isLoading && (
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid #c3ccdc',
                    borderTopColor: '#2a62ff',
                    display: 'inline-block',
                    animation: 'ng-spin 0.75s linear infinite',
                  }}
                />
              )}
              {message}
            </div>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <button
                type="button"
                className="button_05 button_gray"
                onClick={() => window.close()}
              >
                {msg.close}
              </button>
              <button
                type="submit"
                className="button_05 button_blue"
                disabled={isLoading}
              >
                {isLoading ? msg.verifying : msg.confirm}
              </button>
            </div>
          </form>
        </div>
      )}

      <style>
        {`
          @keyframes ng-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
